import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { currentSessionScope } from '../services/session-scope';
import { offlineDb } from '../offline/offline-db';
import { OfflineService } from '../offline/offline.service';

/** Status 0 is what the browser reports when the request never left. */
function isNetworkFailure(error: HttpErrorResponse): boolean {
  return error.status === 0;
}

/**
 * Keeps the clinical screens usable without a network.
 *
 * Reads are mirrored into IndexedDB as they succeed and served from there when
 * the request cannot leave the device. Writes that cannot leave are parked in
 * an outbox and replayed, in order, once the connection returns — so a
 * consultation recorded during a power cut is not lost work.
 *
 * Scope is limited to `OFFLINE_PATHS`; everything else fails normally.
 */
export const offlineInterceptor: HttpInterceptorFn = (req, next) => {
  const offline = inject(OfflineService);

  // The sync service's own replays must pass straight through.
  if (req.headers.has('X-Offline-Replay')) return next(req);
  const scope = currentSessionScope();
  if (!scope || !offline.handles(req.url)) return next(req);

  if (req.method === 'GET') {
    return next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          void offline.cacheResponse(req.urlWithParams, event.body, scope);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (!isNetworkFailure(error)) return throwError(() => error);
        return from(offline.readCache(req.urlWithParams, scope)).pipe(
          switchMap((cached) => {
            if (!cached || scope !== currentSessionScope()) return throwError(() => error);
            return of(
              new HttpResponse({
                body: cached.body,
                status: 200,
                // Lets a component tell live data from a local copy.
                headers: req.headers.set('X-From-Offline-Cache', '1'),
                url: req.url,
              }) as HttpEvent<unknown>,
            );
          }),
        );
      }),
    );
  }

  return next(req).pipe(
    tap(event => { if (event instanceof HttpResponse) void offlineDb.clearCache().catch(() => undefined); }),
    catchError((error: HttpErrorResponse) => {
      if (!isNetworkFailure(error)) return throwError(() => error);
      return from(
        offline.queueMutation(req.method, req.urlWithParams, req.body, scope),
      ).pipe(
        switchMap(() =>
          of(
            new HttpResponse({
              // 202: accepted locally, not yet applied on the server. Components
              // treat it as success; the pending badge tells the whole truth.
              status: 202,
              body: { queuedOffline: true, ...(req.body as object) },
              url: req.url,
            }) as HttpEvent<unknown>,
          ),
        ),
      );
    }),
  ) as Observable<HttpEvent<unknown>>;
};
